/**
 * Firecrawl API wrapper — proxied through iam_service to keep API keys server-side.
 *
 * The browser never receives REACT_APP_FIRECRAWL_API_KEY. The three outbound
 * Firecrawl calls are routed through POST /api/v1/proxy/firecrawl/* on the IAM
 * backend, which injects the Bearer token before forwarding to api.firecrawl.dev.
 *
 * checkDomainSecurity uses Google DNS-over-HTTPS (no API key) and stays client-side.
 * analyzeContentPatterns is a pure local function — no network call.
 */

import axios from 'axios';
import API from '../apiClient';

/**
 * Search and scrape in one call — returns up to `limit` results with markdown content.
 */
export const firecrawlSearchAndScrape = (query, limit = 20) =>
  API.post('/proxy/firecrawl/v2/search', {
    query,
    limit,
    scrapeOptions: { formats: ['markdown'] },
  }).then((r) => r.data);

/**
 * Standalone scrape for a single URL.
 */
export const firecrawlScrape = (url) =>
  API.post('/proxy/firecrawl/v1/scrape', {
    url,
    formats: ['markdown', 'links'],
  }).then((r) => r.data);

/**
 * Map a website — returns all discovered URLs (sitemap).
 */
export const firecrawlMap = (url, limit = 100) =>
  API.post('/proxy/firecrawl/v1/map', {
    url,
    limit,
    includeSubdomains: true,
  }).then((r) => r.data);

/**
 * Check DMARC, SPF and basic DNS records via Google DNS-over-HTTPS.
 * No API key required — safe to call from the browser.
 */
export const checkDomainSecurity = async (domain) => {
  const cleanDomain = domain.replace(/^https?:\/\//, '').replace(/\/.*$/, '').trim();

  const fetchRecord = async (name, type) => {
    try {
      const resp = await axios.get(
        `https://dns.google/resolve?name=${encodeURIComponent(name)}&type=${type}`,
        { headers: {} }
      );
      return resp.data?.Answer || [];
    } catch {
      return [];
    }
  };

  const [dmarcRecords, spfRecords, mxRecords] = await Promise.all([
    fetchRecord(`_dmarc.${cleanDomain}`, 'TXT'),
    fetchRecord(cleanDomain, 'TXT'),
    fetchRecord(cleanDomain, 'MX'),
  ]);

  const spfRecord = spfRecords.find((r) => r?.data?.includes('v=spf1'));
  const dmarcRecord = dmarcRecords.find((r) => r?.data?.includes('v=DMARC1'));

  const dmarcData = dmarcRecord?.data || '';
  const dmarcPolicy = dmarcData.match(/p=([^;]+)/)?.[1] || null;

  return {
    domain: cleanDomain,
    hasDmarc: !!dmarcRecord,
    dmarcPolicy,
    dmarcRecord: dmarcData,
    hasSpf: !!spfRecord,
    spfRecord: spfRecord?.data || '',
    hasMx: mxRecords.length > 0,
    mxRecords: mxRecords.map((r) => r?.data).filter(Boolean),
    powerdmarcUrl: `https://powerdmarc.com/dmarc-record-checker/?domain=${cleanDomain}`,
  };
};

/**
 * Analyse scraped markdown content for patterns:
 * technologies, social links, contact info, industry keywords.
 * Pure local function — no network call.
 */
const ORG_LOOKUP_SCHEMA = {
  type: 'object',
  properties: {
    uen: { type: 'string' },
    registered_entity_name: { type: 'string' },
    former_names: { type: 'array', items: { type: 'string' } },
    entity_type: { type: 'string' },
    business_constitution: { type: 'string' },
    company_type: { type: 'string' },
    status: { type: 'string' },
    registration_date: { type: 'string' },
    uen_issue_date: { type: 'string' },
    registered_address: { type: 'string' },
    postal_code: { type: 'string' },
    primary_ssic_code: { type: 'string' },
    primary_ssic_description: { type: 'string' },
    secondary_ssic_code: { type: 'string' },
    secondary_ssic_description: { type: 'string' },
    no_of_officers: { type: 'number' },
    website: { type: 'string' },
  },
};

const UEN_PATTERN = /^[0-9]{7,10}[A-Z]$/i;

const unwrapSearchWebResults = (searchResponse) => {
  if (!searchResponse) return [];
  if (Array.isArray(searchResponse.data?.web)) return searchResponse.data.web;
  if (Array.isArray(searchResponse.web)) return searchResponse.web;
  if (Array.isArray(searchResponse.data)) return searchResponse.data;
  if (Array.isArray(searchResponse)) return searchResponse;
  return [];
};

const getResultJson = (result) => {
  if (!result || typeof result !== 'object') return null;
  return result.json || result.data?.json || result.scrape?.json || null;
};

const getResultUrl = (result) =>
  result?.url || result?.metadata?.sourceURL || result?.metadata?.url || '';

const getHostname = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const normalizeUen = (uen) => String(uen || '').trim().toUpperCase().replace(/\s+/g, '');

const normalizeCompanyName = (name) =>
  String(name || '')
    .toUpperCase()
    .replace(/PRIVATE LIMITED/g, 'PTE LTD')
    .replace(/PTE\.?\s*LTD\.?/g, 'PTE LTD')
    .replace(/[^A-Z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const scoreOrgResult = (result, index) => {
  const json = getResultJson(result);
  const uen = normalizeUen(json?.uen);
  const hostname = getHostname(getResultUrl(result));
  let score = 1000 - index;
  if (hostname.includes('sgpbusiness.com')) score += 1000;
  if (UEN_PATTERN.test(uen)) score += 500;
  if (json?.registered_entity_name) score += 100;
  if (json?.registered_address) score += 50;
  return score;
};

/** Build the Firecrawl search query shown in UI and sent to the API. */
export const buildOrgLookupQuery = (organizationName) => {
  const trimmed = String(organizationName || '').trim();
  return trimmed ? `"${trimmed}" UEN` : '';
};

/**
 * Search Singapore business directories for organization registration details.
 */
export const firecrawlOrgLookup = (organizationName) => {
  const trimmed = String(organizationName || '').trim();
  if (!trimmed) {
    return Promise.reject(new Error('Organization name is required'));
  }
  return API.post('/proxy/firecrawl/v2/search', {
    query: buildOrgLookupQuery(trimmed),
    limit: 8,
    scrapeOptions: {
      formats: [{
        type: 'json',
        schema: ORG_LOOKUP_SCHEMA,
        prompt:
          'This is a Singapore company registry / business directory page (e.g. sgpbusiness.com or ACRA). Extract the company registration fields exactly as shown; omit fields not present. Dates as YYYY-MM-DD.',
      }],
    },
  }).then((r) => r.data);
};

/**
 * Rank and dedupe Firecrawl search results into top org candidates.
 * @returns {Array<{ uen, registered_entity_name, address, source, url, json }>}
 */
export const rankOrgCandidates = (searchResponse, limit = 3) => {
  const rawResults = unwrapSearchWebResults(searchResponse);
  const scored = [];

  rawResults.forEach((result, index) => {
    const json = getResultJson(result);
    if (!json || typeof json !== 'object') return;

    const uen = normalizeUen(json.uen);
    const registeredEntityName = String(json.registered_entity_name || '').trim();
    if (!uen && !registeredEntityName) return;

    const url = getResultUrl(result);
    scored.push({
      uen,
      registered_entity_name: registeredEntityName,
      address: String(json.registered_address || '').trim(),
      source: getHostname(url) || 'unknown',
      url,
      json,
      score: scoreOrgResult(result, index),
    });
  });

  scored.sort((a, b) => b.score - a.score);

  const seen = new Set();
  const deduped = [];
  for (const candidate of scored) {
    const key = candidate.uen || normalizeCompanyName(candidate.registered_entity_name);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    deduped.push(candidate);
    if (deduped.length >= limit) break;
  }

  return deduped;
};

const BANNED_WEBSITE_HOSTS = [
  'linkedin.com',
  'facebook.com',
  'twitter.com',
  'x.com',
  'instagram.com',
  'wikipedia.org',
  'crunchbase.com',
  'glassdoor',
  'indeed.com',
  'jobstreet',
  'bloomberg.com',
  'opencorporates.com',
  'sgpbusiness.com',
  'sgpgrid.com',
  'maps.google.',
  'youtube.com',
  'gov.sg',
  'acra.gov.sg',
  'findagrave.com',
  'yelp.com',
  'scam.sg',
  'recordowl.com',
  'companies.sg',
  'bizfile.gov.sg',
  'singaporedb.com',
  'streetdirectory.com',
];

const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.com.sg',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'icloud.com',
  'me.com',
  'proton.me',
  'protonmail.com',
  'aol.com',
  'mail.com',
  'zoho.com',
]);

/** Extract a corporate email domain, skipping common free-mail providers. */
export const extractCorporateEmailDomain = (email) => {
  const match = String(email || '')
    .trim()
    .toLowerCase()
    .match(/@([a-z0-9.-]+\.[a-z]{2,})$/i);
  if (!match) return '';
  const domain = match[1].replace(/^www\./, '');
  return FREE_EMAIL_DOMAINS.has(domain) ? '' : domain;
};

const isDirectoryListingPage = (result) => {
  const description = getResultDescription(result).toLowerCase();
  const title = getResultTitle(result).toLowerCase();
  const url = getResultUrl(result).toLowerCase();
  return (
    description.includes('acra registration') ||
    description.includes('has been in operation') ||
    description.includes('based on acra') ||
    description.includes('incorporation in') ||
    /\(\d{8,}[a-z]\)/i.test(title) ||
    /\/\d{8,}[a-z]/i.test(url)
  );
};

const getResultTitle = (result) =>
  String(result?.title || result?.metadata?.title || result?.metadata?.ogTitle || '').trim();

const getResultDescription = (result) =>
  String(
    result?.description ||
      result?.metadata?.description ||
      result?.metadata?.ogDescription ||
      ''
  ).trim();

/** Strip protocol/trailing slash for the primary domain field. */
export const normalizeWebsiteForField = (url) => {
  if (!url) return '';
  return String(url)
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/$/, '')
    .split('/')[0];
};

const isBannedWebsiteHost = (hostname) => {
  const host = String(hostname || '').toLowerCase();
  return BANNED_WEBSITE_HOSTS.some((banned) => host.includes(banned));
};

const scoreWebsiteResult = (result, index, companyName, emailDomain = '') => {
  const url = getResultUrl(result);
  const hostname = getHostname(url);
  if (!hostname || isBannedWebsiteHost(hostname)) return -1;
  if (isDirectoryListingPage(result)) return -1;

  let score = 1000 - index;
  const title = getResultTitle(result).toLowerCase();
  const nameTokens = normalizeCompanyName(companyName)
    .split(' ')
    .filter((token) => token.length > 2);

  nameTokens.forEach((token) => {
    if (hostname.includes(token.toLowerCase())) score += 120;
    if (title.includes(token.toLowerCase())) score += 40;
  });

  if (emailDomain && (hostname === emailDomain || hostname.endsWith(`.${emailDomain}`))) {
    score += 500;
  }

  if (/official|home|about/i.test(title)) score += 30;
  if (hostname.startsWith('www.')) score += 5;

  return score;
};

/**
 * Build Firecrawl website search queries from registered entity and contact email.
 * @returns {Array<{ label, query }>}
 */
export const buildWebsiteLookupQueries = ({
  registeredName = '',
  registrationId = '',
  contactEmail = '',
} = {}) => {
  const entity = String(registeredName || '').trim();
  const uen = String(registrationId || '').trim();
  const emailDomain = extractCorporateEmailDomain(contactEmail);
  const queries = [];

  if (entity) {
    queries.push({
      label: 'Registered entity',
      query: [`"${entity}"`, 'official website', uen].filter(Boolean).join(' '),
    });
    queries.push({
      label: 'Registered entity',
      query: [`"${entity}"`, 'company website', 'Singapore'].join(' '),
    });
  }

  if (emailDomain) {
    queries.push({
      label: 'Contact email domain',
      query: entity ? `"${entity}" site:${emailDomain}` : `site:${emailDomain}`,
    });
  }

  return queries;
};

/** @deprecated Use buildWebsiteLookupQueries */
export const buildWebsiteLookupQuery = (params = {}) => {
  const queries = buildWebsiteLookupQueries(params);
  return queries[0]?.query || '';
};

/**
 * Run multiple Firecrawl searches for website candidates.
 */
export const firecrawlWebsiteLookupMulti = async ({
  registeredName = '',
  registrationId = '',
  contactEmail = '',
}) => {
  const queries = buildWebsiteLookupQueries({ registeredName, registrationId, contactEmail });
  if (!queries.length) {
    return Promise.reject(new Error('Registered entity or contact email is required'));
  }

  const responses = await Promise.all(
    queries.map(async (spec) => {
      try {
        const response = await API.post('/proxy/firecrawl/v2/search', {
          query: spec.query,
          limit: 8,
        });
        return { spec, data: response.data };
      } catch {
        return null;
      }
    })
  );

  return {
    queries,
    responses: responses.filter(Boolean),
  };
};

/** @deprecated Use firecrawlWebsiteLookupMulti */
export const firecrawlWebsiteLookup = (params) =>
  firecrawlWebsiteLookupMulti(params).then((result) => result.responses[0]?.data);

/**
 * Rank and dedupe website candidates from one or more Firecrawl search responses.
 * @returns {Array<{ domain, url, title, description, source, searchLabel }>}
 */
export const rankWebsiteCandidates = (
  searchInput,
  companyName = '',
  limit = 5,
  contactEmail = ''
) => {
  const emailDomain = extractCorporateEmailDomain(contactEmail);
  const responseList = Array.isArray(searchInput?.responses)
    ? searchInput.responses
    : [{ spec: { label: 'Search' }, data: searchInput }];

  const scored = [];

  responseList.forEach(({ spec, data }) => {
    const rawResults = unwrapSearchWebResults(data);
    rawResults.forEach((result, index) => {
      const url = getResultUrl(result);
      const domain = normalizeWebsiteForField(url);
      if (!domain) return;

      const score = scoreWebsiteResult(result, index, companyName, emailDomain);
      if (score < 0) return;

      scored.push({
        domain,
        url: url.startsWith('http') ? url : `https://${domain}`,
        title: getResultTitle(result) || domain,
        description: getResultDescription(result),
        source: getHostname(url) || domain,
        searchLabel: spec?.label || 'Search',
        score: score + (spec?.label === 'Contact email domain' ? 50 : 0),
      });
    });
  });

  if (emailDomain && !isBannedWebsiteHost(emailDomain)) {
    scored.push({
      domain: emailDomain,
      url: `https://${emailDomain}`,
      title: `Suggested from contact email (@${emailDomain})`,
      description: 'Derived from the contact email domain on your account.',
      source: emailDomain,
      searchLabel: 'Contact email domain',
      score: 2000,
    });
  }

  scored.sort((a, b) => b.score - a.score);

  const seen = new Set();
  const deduped = [];
  for (const candidate of scored) {
    const key = candidate.domain.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const { score, ...rest } = candidate;
    deduped.push(rest);
    if (deduped.length >= limit) break;
  }

  return deduped;
};

export const analyzeContentPatterns = (markdownContent, title = '') => {
  const text = (markdownContent || '') + ' ' + title;

  const techPatterns = [
    'React', 'Angular', 'Vue', 'Next.js', 'Node.js', 'Python', 'AWS', 'Azure',
    'Google Cloud', 'Kubernetes', 'Docker', 'Salesforce', 'SAP', 'HubSpot',
    'WordPress', 'Shopify', 'Stripe', 'Twilio',
  ];

  const industryPatterns = [
    'compliance', 'cybersecurity', 'fintech', 'healthcare', 'logistics',
    'manufacturing', 'retail', 'SaaS', 'B2B', 'enterprise', 'startup',
    'banking', 'insurance', 'legal', 'consulting', 'government',
  ];

  const foundTech = techPatterns.filter((t) =>
    new RegExp(`\\b${t}\\b`, 'i').test(text)
  );

  const foundIndustry = industryPatterns.filter((k) =>
    new RegExp(`\\b${k}\\b`, 'i').test(text)
  );

  const emailMatches = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g) || [];
  const phoneMatches = text.match(/\+?[\d\s\-().]{10,}/g) || [];

  const socialLinks = {
    linkedin: /linkedin\.com/i.test(text),
    twitter: /twitter\.com|x\.com/i.test(text),
    facebook: /facebook\.com/i.test(text),
    instagram: /instagram\.com/i.test(text),
    youtube: /youtube\.com/i.test(text),
  };

  return {
    technologies: [...new Set(foundTech)],
    industryKeywords: [...new Set(foundIndustry)],
    emails: [...new Set(emailMatches)].slice(0, 5),
    phones: [...new Set(phoneMatches.map((p) => p.trim()))].slice(0, 3),
    socialPresence: socialLinks,
  };
};
